import { motion } from "framer-motion";
import { FileText, Truck, Droplets, PackageCheck, ArrowRight } from "lucide-react";

const steps = [
  {
    n: "01",
    icon: FileText,
    title: "Dogovor i procena",
    desc: "Analiziramo tip veša, procenjujemo količinu, definišemo dinamiku preuzimanja i formiramo fiksnu ugovorenu cenu bez skrivenih troškova.",
    tag: "Besplatna procena",
  },
  {
    n: "02",
    icon: Truck,
    title: "Preuzimamo u dogovoru",
    desc: "Naše vozilo dolazi po fiksnom rasporedu — dnevno, više puta nedeljno ili po pozivu. Bez čekanja i bez remećenja vašeg posla.",
    tag: "U cenu saradnje",
  },
  {
    n: "03",
    icon: Droplets,
    title: "Perimo profesionalno",
    desc: "Industrijske mašine, kontrolisana temperatura, sredstva prilagođena tipu tkanine. Beli veš odvojeno, obojeni odvojeno, uniforme posebno.",
    tag: "24 – 48h obrada",
  },
  {
    n: "04",
    icon: PackageCheck,
    title: "Vraćamo spremno",
    desc: "Ispeglano, sortirano, upakovano u zaštitne navlake i dostavljeno na vašu adresu — spremno da odmah ide na sto, u sobu ili osoblju.",
    tag: "U vaš termin",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="process-section tw-section relative overflow-hidden"
    >
      <div className="process-section__glow" aria-hidden />
      <div className="absolute inset-0 dots-bg opacity-20 pointer-events-none" />

      <div className="relative z-10 tw-container">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-14 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="overline">03 — Kako radimo</div>
            <h2 className="mt-4 md:mt-6 tw-heading-section">
              Četiri koraka.
              <br />
              <span className="blue-text font-display italic">Nula briga.</span>
            </h2>
            <p className="mt-5 sm:mt-6 text-[#b8c9e4] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Proces je dizajniran da prati ritam vašeg poslovanja — od prve
              procene, preko preuzimanja i pranja, do isporuke spremnog tekstila u
              dogovorenom terminu.
            </p>
          </motion.div>
        </div>

        {/* Progress rail — desktop */}
        <div className="hidden lg:flex process-progress mb-10" aria-hidden>
          {steps.map((step, i) => (
            <div key={step.n} className="process-progress__segment">
              <span className="process-progress__dot">{step.n}</span>
              {i < steps.length - 1 && <span className="process-progress__line" />}
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="process-grid" data-testid="how-it-works-timeline">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.article
                key={step.n}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`timeline-step-${i}`}
                className="process-card group"
              >
                <span className="process-card__watermark" aria-hidden>
                  {step.n}
                </span>

                <div className="process-card__head">
                  <div className="process-card__icon">
                    <Icon size={22} strokeWidth={1.7} className="text-[#a7e2ff]" />
                  </div>
                  <span className="process-card__index lg:hidden">{step.n}</span>
                </div>

                <h3 className="process-card__title">{step.title}</h3>
                <p className="process-card__desc">{step.desc}</p>

                <div className="process-card__footer">
                  <span className="process-card__tag">{step.tag}</span>
                  {i < steps.length - 1 && (
                    <ArrowRight
                      size={16}
                      className="process-card__arrow hidden lg:block text-[#55d0ff]/50 group-hover:text-[#55d0ff] group-hover:translate-x-1 transition-all"
                      aria-hidden
                    />
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 sm:mt-12 text-center"
        >
          <a href="#contact" className="tw-btn">
            Započnite saradnju <ArrowRight size={16} strokeWidth={2.5} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
